import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2, ArrowRight, Lock, Mail } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WORDS = [
  "hello","नमस्ते","bonjour","hola","வணக்கம்","こんにちは",
  "привет","안녕","مرحبا","你好","ciao","olá","salut","hej",
  "あ","अ","字","Ж","한","أ","α","漢","ப","Б","Σ","ω",
  "가","ع","文","글","η","ζ","Ш","อ","β","ξ","ক","ස",
];

const S = `
.cl-root{position:relative;min-height:100vh;overflow:hidden;background:#0B0F19;
  font-family:'Manrope',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#fff}
.cl-stars{position:absolute;inset:0;z-index:0}
.cl-words{position:absolute;inset:0;z-index:1;overflow:hidden;pointer-events:none}
.cl-w{position:absolute;will-change:transform,opacity;pointer-events:none;user-select:none;line-height:1;font-weight:500;transition:none}
.cl-grid{position:absolute;inset:0;z-index:1;pointer-events:none;
  background-image:linear-gradient(rgba(255,255,255,.018) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.018) 1px,transparent 1px);
  background-size:72px 72px}
.cl-glow1{position:absolute;top:-15%;right:-10%;width:600px;height:600px;border-radius:50%;
  background:radial-gradient(circle,rgba(99,102,241,.07),transparent 70%);filter:blur(60px);pointer-events:none;z-index:1;animation:cl-d1 20s ease-in-out infinite}
.cl-glow2{position:absolute;bottom:-15%;left:-10%;width:500px;height:500px;border-radius:50%;
  background:radial-gradient(circle,rgba(14,165,233,.05),transparent 70%);filter:blur(60px);pointer-events:none;z-index:1;animation:cl-d2 25s ease-in-out infinite}
@keyframes cl-d1{0%,100%{transform:translate(0,0)}50%{transform:translate(-40px,30px)}}
@keyframes cl-d2{0%,100%{transform:translate(0,0)}50%{transform:translate(30px,-25px)}}

/* center layout */
.cl-center{position:relative;z-index:10;display:flex;flex-direction:column;align-items:center;
  justify-content:center;min-height:100vh;padding:24px}

/* entrance */
@keyframes cl-fu{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
.cl-e{animation:cl-fu .6s ease-out forwards;opacity:0}
.cl-e1{animation-delay:.1s}
.cl-e2{animation-delay:.2s}
.cl-e3{animation-delay:.3s}

/* logo */
.cl-logo{width:52px;height:52px;border-radius:14px;object-fit:cover;margin-bottom:14px;
  border:1px solid rgba(255,255,255,.08);box-shadow:0 2px 16px rgba(0,0,0,.3)}
.cl-logo-fb{width:52px;height:52px;border-radius:14px;background:linear-gradient(135deg,#6366F1,#0EA5E9);
  display:flex;align-items:center;justify-content:center;font-weight:800;font-size:20px;margin-bottom:14px;
  box-shadow:0 2px 16px rgba(0,0,0,.3)}

/* title */
.cl-title{font-size:clamp(24px,4vw,36px);font-weight:800;letter-spacing:0em;color:#fff;margin-bottom:4px;text-align:center}

/* subtitle */
.cl-sub{font-size:13px;color:rgba(255,255,255,.35);margin-bottom:24px;text-align:center}

/* card */
@keyframes cl-cg{0%,100%{box-shadow:0 0 0 0 transparent}50%{box-shadow:0 0 40px rgba(99,102,241,.04)}}
.cl-card{width:100%;max-width:400px;background:rgba(255,255,255,.04);backdrop-filter:blur(16px);
  -webkit-backdrop-filter:blur(16px);border:1px solid rgba(255,255,255,.08);border-radius:16px;padding:28px;
  animation:cl-fu .6s ease-out .3s forwards,cl-cg 6s ease-in-out 1s infinite;opacity:0}

/* fields */
.cl-field{margin-bottom:20px}
.cl-field label{display:block;font-size:11px;font-weight:600;color:rgba(255,255,255,.4);
  text-transform:uppercase;letter-spacing:.12em;margin-bottom:8px}
.cl-iw{position:relative;border-radius:10px;transition:box-shadow .3s}
.cl-iw:focus-within{box-shadow:0 0 0 2px rgba(99,102,241,.25),0 0 20px rgba(99,102,241,.06)}
.cl-iw svg{position:absolute;left:14px;top:50%;transform:translateY(-50%);width:16px;height:16px;
  color:rgba(255,255,255,.2);pointer-events:none;z-index:1}
.cl-inp{width:100%;height:46px;padding:0 14px 0 42px;background:rgba(255,255,255,.03);
  border:1px solid rgba(255,255,255,.07);border-radius:10px;color:#fff;font-size:14px;
  font-family:inherit;outline:none;transition:border-color .2s,background .2s}
.cl-inp::placeholder{color:rgba(255,255,255,.2)}
.cl-inp:focus{border-color:rgba(99,102,241,.35);background:rgba(255,255,255,.04)}

/* submit */
.cl-submit{width:100%;height:46px;border:none;border-radius:10px;cursor:pointer;
  background:linear-gradient(135deg,#6366F1,#4F46E5);color:#fff;font-size:14px;font-weight:600;
  font-family:inherit;letter-spacing:.03em;display:flex;align-items:center;justify-content:center;gap:8px;
  transition:all .25s;box-shadow:0 2px 12px rgba(99,102,241,.25)}
.cl-submit:hover:not(:disabled){box-shadow:0 4px 20px rgba(99,102,241,.35);transform:translateY(-1px)}
.cl-submit:active:not(:disabled){transform:translateY(0)}
.cl-submit:disabled{opacity:.6;cursor:wait}
.cl-submit svg{transition:transform .2s}
.cl-submit:hover:not(:disabled) svg{transform:translateX(2px)}

/* divider */
.cl-div{display:flex;align-items:center;gap:12px;margin:24px 0 20px}
.cl-div-line{flex:1;height:1px;background:rgba(255,255,255,.06)}
.cl-div-text{font-size:10px;color:rgba(255,255,255,.2);text-transform:uppercase;letter-spacing:.15em}

/* ticker */
@keyframes cl-tick{from{transform:translateX(0)}to{transform:translateX(-50%)}}
.cl-ticker{overflow:hidden;border-radius:10px;background:rgba(255,255,255,.02);
  border:1px solid rgba(255,255,255,.04);padding:10px 0;margin-top:4px}
.cl-track{display:flex;gap:24px;animation:cl-tick 30s linear infinite;white-space:nowrap;width:max-content}
.cl-ti{font-size:12px;color:rgba(255,255,255,.18);font-weight:500}

/* footer */
.cl-footer{position:absolute;bottom:16px;left:0;right:0;text-align:center;z-index:5}
.cl-footer p{font-size:10px;color:rgba(255,255,255,.12);line-height:1.7}
`;

const Starfield = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    let stars = [], raf;
    const init = () => {
      c.width = window.innerWidth; c.height = window.innerHeight;
      stars = Array.from({ length: 160 }, () => ({
        x: Math.random() * c.width, y: Math.random() * c.height,
        r: Math.random() * 1.1 + 0.15, a: Math.random() * 0.4 + 0.1,
        p: Math.random() * Math.PI * 2, s: Math.random() * 0.003 + 0.001,
      }));
    };
    init(); window.addEventListener("resize", init);
    let t = 0;
    const draw = () => {
      t++; ctx.clearRect(0, 0, c.width, c.height);
      for (const s of stars) {
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(180,200,255,${Math.max(0, s.a + Math.sin(t * s.s + s.p) * 0.2)})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { window.removeEventListener("resize", init); cancelAnimationFrame(raf); };
  }, []);
  return <canvas ref={ref} className="cl-stars" />;
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [logo, setLogo] = useState(null);
  const [mounted, setMounted] = useState(false);
  const { login: authLogin } = useAuth();
  const navigate = useNavigate();

  const wordElsRef = useRef([]);
  const wordDataRef = useRef([]);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  const animRef = useRef(null);
  const sizeRef = useRef({ w: window.innerWidth, h: window.innerHeight });

  useEffect(() => { setMounted(true); }, []);
  useEffect(() => { axios.get(`${API}/settings`).then(r => { if (r.data?.logo_url) setLogo(r.data.logo_url); }).catch(() => {}); }, []);

  const wordConfigs = useMemo(() => {
    const n = window.innerWidth < 768 ? 16 : 30;
    return Array.from({ length: n }, (_, i) => ({
      id: i, text: WORDS[i % WORDS.length],
      opacity: 0.06 + Math.random() * 0.08,
      fontSize: WORDS[i % WORDS.length].length > 2 ? 12 + Math.random() * 4 : 16 + Math.random() * 10,
    }));
  }, []);

  const tickerWords = useMemo(() => {
    const b = ["hello","नमस्ते","bonjour","hola","வணக்கம்","こんにちは","привет","안녕","مرحبا","你好","ciao","olá","salut","hej","danke","obrigado"];
    return [...b, ...b];
  }, []);

  useEffect(() => {
    const W = window.innerWidth, H = window.innerHeight;
    sizeRef.current = { w: W, h: H };
    wordDataRef.current = wordConfigs.map(() => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4,
      rot: (Math.random() - 0.5) * 10, rs: (Math.random() - 0.5) * 0.05,
    }));
    const loop = () => {
      const d = wordDataRef.current, els = wordElsRef.current;
      const mx = mouseRef.current.x, my = mouseRef.current.y;
      const { w: W, h: H } = sizeRef.current;
      for (let i = 0; i < d.length; i++) {
        const w = d[i], el = els[i];
        if (!w || !el) continue;
        w.rot += w.rs;
        const dx = w.x - mx, dy = w.y - my, dist = Math.hypot(dx, dy);
        if (dist < 140 && dist > 1) { const f = ((140 - dist) / 140) * 0.2; w.vx += (dx / dist) * f; w.vy += (dy / dist) * f; }
        w.x += w.vx; w.y += w.vy; w.vx *= 0.999; w.vy *= 0.999;
        if (w.x < -50) w.x += W + 100; if (w.x > W + 50) w.x -= W + 100;
        if (w.y < -50) w.y += H + 100; if (w.y > H + 50) w.y -= H + 100;
        el.style.transform = `translate(${w.x}px,${w.y}px) rotate(${w.rot}deg)`;
      }
      animRef.current = requestAnimationFrame(loop);
    };
    animRef.current = requestAnimationFrame(loop);
    const onR = () => { sizeRef.current = { w: window.innerWidth, h: window.innerHeight }; };
    window.addEventListener("resize", onR);
    return () => { cancelAnimationFrame(animRef.current); window.removeEventListener("resize", onR); };
  }, [wordConfigs]);

  const handleMouse = useCallback((e) => { mouseRef.current = { x: e.clientX, y: e.clientY }; }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please fill all fields"); return; }
    setLoading(true);
    try {
      const user = await authLogin(email, password);
      if (user.role === "admin") { navigate("/"); toast.success("Welcome back"); }
      else { navigate("/portal"); toast.success(`Welcome, ${user.display_name || user.email}`); }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally { setLoading(false); }
  };

  return (
    <div className="cl-root" onMouseMove={handleMouse} data-testid="login-page">
      <style>{S}</style>
      <Starfield />
      <div className="cl-grid" />
      <div className="cl-glow1" />
      <div className="cl-glow2" />

      <div className="cl-words">
        {wordConfigs.map(w => (
          <span key={w.id} ref={el => { wordElsRef.current[w.id] = el; }} className="cl-w"
            style={{ color: `rgba(255,255,255,${w.opacity})`, fontSize: w.fontSize }}>{w.text}</span>
        ))}
      </div>

      <div className="cl-center">
        {/* logo */}
        <div className={mounted ? "cl-e" : "opacity-0"}>
          {logo ? <img src={logo} alt="" className="cl-logo" /> : <div className="cl-logo-fb">G</div>}
        </div>

        {/* title */}
        <h1 className={`cl-title ${mounted ? "cl-e cl-e1" : "opacity-0"}`} data-testid="login-title">GRAPHIKPRO</h1>
        <p className={`cl-sub ${mounted ? "cl-e cl-e2" : "opacity-0"}`}>Multilingual invoice platform</p>

        {/* card */}
        <div className="cl-card" data-testid="login-card">
          <form onSubmit={handleSubmit}>
            <div className="cl-field">
              <label>Email</label>
              <div className="cl-iw">
                <Mail />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@email.com" className="cl-inp" data-testid="login-email-input" autoComplete="email" />
              </div>
            </div>
            <div className="cl-field">
              <label>Password</label>
              <div className="cl-iw">
                <Lock />
                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password" className="cl-inp" data-testid="login-password-input" autoComplete="current-password" />
              </div>
            </div>
            <button type="submit" disabled={loading} className="cl-submit" data-testid="login-submit-btn">
              {loading ? <><Loader2 size={16} className="animate-spin" />Signing in...</> : <>Sign In<ArrowRight size={16} /></>}
            </button>
          </form>

          <div className="cl-div">
            <div className="cl-div-line" />
            <span className="cl-div-text">Languages we connect</span>
            <div className="cl-div-line" />
          </div>
          <div className="cl-ticker">
            <div className="cl-track">
              {tickerWords.map((w, i) => <span key={i} className="cl-ti">{w}</span>)}
            </div>
          </div>
        </div>
      </div>

      <div className="cl-footer">
        <p>&copy; 2026 Graphikpro. All rights reserved.</p>
      </div>
    </div>
  );
};

export default Login;
